import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  createPosUser,
  updatePosUser,
  resolvePosUserId,
} from "app/services/api/accessSystemPosUsers";

const emptyForm = () => ({
  firstName: "",
  lastName: "",
  pin: "",
  active: true,
});

const validatePin = (pin) => /^\d{4}$/.test(String(pin || "").trim());

const buildErrors = (t, values) => {
  const e = {};
  const fn = String(values.firstName || "").trim();
  const ln = String(values.lastName || "").trim();
  const pin = String(values.pin || "").trim();
  if (!fn) {
    e.firstName = t("pages.pos.users.validation.firstNameRequired");
  }
  if (!ln) {
    e.lastName = t("pages.pos.users.validation.lastNameRequired");
  }
  if (!pin) {
    e.pin = t("pages.pos.users.validation.pinRequired");
  } else if (!validatePin(pin)) {
    e.pin = t("pages.pos.users.validation.pinDigits");
  }
  return e;
};

const diffPayload = (initial, current) => {
  const out = {};
  if (String(initial.firstName ?? "").trim() !== String(current.firstName ?? "").trim()) {
    out.firstName = String(current.firstName ?? "").trim();
  }
  if (String(initial.lastName ?? "").trim() !== String(current.lastName ?? "").trim()) {
    out.lastName = String(current.lastName ?? "").trim();
  }
  if (String(initial.pin ?? "").trim() !== String(current.pin ?? "").trim()) {
    out.pin = String(current.pin ?? "").trim();
  }
  if (Boolean(initial.active) !== Boolean(current.active)) {
    out.active = Boolean(current.active);
  }
  return out;
};

const PosUserFormModal = ({ open, mode, user, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [values, setValues] = useState(emptyForm);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const title = useMemo(
    () =>
      mode === "edit"
        ? t("pages.pos.users.modalEditTitle")
        : t("pages.pos.users.modalCreateTitle"),
    [mode, t],
  );

  useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    if (mode === "edit" && user) {
      const next = {
        firstName: user.firstName ?? user.first_name ?? "",
        lastName: user.lastName ?? user.last_name ?? "",
        pin: user.pin != null ? String(user.pin) : "",
        active: user.active !== false,
      };
      setValues(next);
      setInitialSnapshot({ ...next });
    } else {
      const next = emptyForm();
      setValues(next);
      setInitialSnapshot({ ...next });
    }
  }, [open, mode, user]);

  const handleGeneratePin = () => {
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    setValues((v) => ({ ...v, pin }));
    setFieldErrors((e) => ({ ...e, pin: undefined }));
  };

  const handleChange = (field) => (ev) => {
    const v = field === "active" ? ev.target.checked : ev.target.value;
    setValues((prev) => ({ ...prev, [field]: v }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const errs = buildErrors(t, values);
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    try {
      if (mode === "create") {
        await createPosUser({
          firstName: String(values.firstName).trim(),
          lastName: String(values.lastName).trim(),
          pin: String(values.pin).trim(),
          active: Boolean(values.active),
        });
        enqueueSnackbar(t("pages.pos.users.saveSuccess"), { variant: "success" });
        onSaved?.();
        onClose?.();
      } else {
        const id = resolvePosUserId(user);
        if (!id) {
          enqueueSnackbar(t("pages.pos.users.errorMissingId"), { variant: "error" });
          return;
        }
        const payload = diffPayload(initialSnapshot, values);
        if (Object.keys(payload).length === 0) {
          onClose?.();
          return;
        }
        await updatePosUser(id, payload);
        enqueueSnackbar(t("pages.pos.users.saveSuccess"), { variant: "success" });
        onSaved?.();
        onClose?.();
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("pages.pos.users.saveError");
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            required
            fullWidth
            label={t("pages.pos.users.fieldFirstName")}
            value={values.firstName}
            onChange={handleChange("firstName")}
            error={Boolean(fieldErrors.firstName)}
            helperText={fieldErrors.firstName || ""}
          />
          <TextField
            required
            fullWidth
            label={t("pages.pos.users.fieldLastName")}
            value={values.lastName}
            onChange={handleChange("lastName")}
            error={Boolean(fieldErrors.lastName)}
            helperText={fieldErrors.lastName || ""}
          />
          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                required
                fullWidth
                label={t("pages.pos.users.fieldPin")}
                value={values.pin}
                onChange={handleChange("pin")}
                inputProps={{ inputMode: "numeric", maxLength: 4 }}
                error={Boolean(fieldErrors.pin)}
                helperText={fieldErrors.pin || ""}
              />
              <Button variant="outlined" onClick={handleGeneratePin} sx={{ mt: 1, flexShrink: 0 }}>
                {t("pages.pos.users.generatePin")}
              </Button>
            </Stack>
          </Box>
          <FormControlLabel
            control={
              <Switch checked={Boolean(values.active)} onChange={handleChange("active")} />
            }
            label={t("pages.pos.users.fieldActive")}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t("buttons.cancel")}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {t("buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PosUserFormModal;
